import { createStyles } from '@mantine/core';
/**
 * This file contains all classes used in notification module
 */
const useStyles = createStyles((theme) => ({
  // used for filled variant, change selected item background color in multi select components
  fillSelectedBackgroundColor: {
    backgroundColor: theme.colors.lightColors[4],
    color: theme.colors.lightColors[0]
  },
  // Change selected item color in multi select components
  selectedBackgroundColor: { backgroundColor: theme.colors.lightColors[4] }, // backgroundColor: 'rgba(255,255,255,0.14)'
  // Used to set backgroundcolor of dropdown select component to dark color
  dropdownBackgroundColor: { backgroundColor: theme.colors.darkColors[0] },
  // Used to set item color of dropdown select component to light color
  itemColorLight: { color: theme.colors.lightColors[0] },
  searchInput: { margin: '0 0 0 4px' },
  // Change input field border color on focus
  inputFocusBorderColor: {
    '&:focus-within': {
      borderColor: theme.colors.primaryColors[0]
    }
  },
  // Filled color select
  fillInputFocusBorderColor: {
    backgroundColor: 'rgba(29,43,57,0.05)',
    borderColor: 'transparent',
    '&:focus-within': {
      borderColor: theme.colors.primaryColors[0]
    }
  },

  // Icon card
  iconCard: {
    backgroundColor: theme.colors.lightColors[0],
    '&:hover': {
      backgroundColor: theme.colors.primaryColors[0]
    }
  },

  // Error Toast
  errorToast: {
    color: theme.colors.darkColors[0],
    backgroundColor: theme.colors.infoColors[1]
  },

  // Success Toast
  successToast: {
    color: theme.colors.lightColors[0],
    backgroundColor: theme.colors.infoColors[2]
  },

  // Individual Colors
  primaryColor: {
    backgroundColor: theme.colors.primaryColors[0]
  },
  primaryLight: {
    backgroundColor: theme.colors.primaryColors[2]
  },
  backgroundFill: {
    backgroundColor: theme.colors.darkColors[0]
  },
  lightColor: {
    backgroundColor: theme.colors.lightColors[0]
  },
  secondaryLight: {
    backgroundColor: theme.colors.secondaryColors[2]
  },

  // Container Shadow
  containerShadow: {
    boxShadow: theme.shadows.md
  },
  textLight: {
    color: theme.colors.lightColors[0]
  },
  sidebarBorderColor: {
    borderTopColor: theme.colors.lightColors[3]
  },
  tabComponent: {
    color: theme.colors.lightColors[1],
    borderColor: theme.colors.darkColors[3],
    '& svg': { width: '24px' },

    '&:hover': {
      background:
        'linear-gradient(90deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 100%)',
      borderLeftColor: theme.colors.secondaryColors[0]
    },
    '&:hover svg': {
      stroke: theme.colors.secondaryColors[0],
      fill: theme.colors.secondaryColors[0]
    }
  },
  activeTabComponent: {
    borderLeftColor: theme.colors.secondaryColors[0],
    background:
      'linear-gradient(90deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 100%)',
    '& svg': {
      stroke: theme.colors.secondaryColors[0],
      fill: theme.colors.secondaryColors[0]
    }
  },

  // Clear filter button
  clearFilterButton: {
    backgroundColor: theme.colors.secondaryColors[2],
    color: theme.colors.secondaryColors[0],
    fontSize: theme.fontSizes.sm,
    fontWeight: 400,
    lineHeight: theme.other.lineHeights.md,
    height: '30px',
    padding: '0 12px',
    '&:hover': {
      backgroundColor: theme.colors.secondaryColors[0],
      color: theme.colors.lightColors[0]
    }
  },

  //  ========= Date Picker Style ====
  datePickerDay: {
    color: theme.colors.lightColors[0],
    '&:hover': {
      backgroundColor: theme.colors.lightColors[4]
    },
    '&:selected': {
      backgroundColor: theme.colors.primaryColors[0]
    }
  },
  datePickerCalendarHeaderLevel: {
    color: theme.colors.lightColors[0],
    '&:hover': {
      backgroundColor: theme.colors.lightColors[4]
    }
  },
  datePickerCalendarHeaderControl: {
    color: theme.colors.lightColors[0],
    '&:hover': {
      backgroundColor: theme.colors.lightColors[4]
    }
  },

  datePickerMonthPickerControl: {
    color: theme.colors.lightColors[0],
    '&:hover': {
      backgroundColor: theme.colors.lightColors[4]
    }
  },
  datePickerMonthPickerControlActive: {
    backgroundColor: theme.colors.primaryColors[0]
  },
  datePickerYearPickerControl: {
    color: theme.colors.lightColors[0],
    '&:hover': {
      backgroundColor: theme.colors.lightColors[4]
    }
  },
  datePickerYearPickerControlActive: {
    backgroundColor: theme.colors.primaryColors[0]
  },
  // ========== Date Picker style end ==========

  // === Page header styles ===
  // Back Button
  buttonBack: {
    height: '44px',
    width: '44px',
    borderWidth: 1,
    borderColor: theme.colors.darkColors[3],
    borderRadius: '50%',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent'
    },
    // Media query with value from theme
    [`@media (max-width: ${theme.breakpoints.md}px)`]: {
      height: '38px',
      width: '38px'
    }
  },
  // Header Button
  headerPrimaryButton: {
    backgroundColor: theme.colors.primaryColors[0],
    boxShadow: theme.shadows.xs,
    fontSize: theme.fontSizes.md,
    fontWeight: 400,
    lineHeight: theme.other.lineHeights.md,
    height: '36px',
    padding: '0 32px',
    '&:hover': {
      backgroundColor: theme.colors.primaryColors[1]
    },

    // Media query with value from theme
    [`@media (max-width: ${theme.breakpoints.md}px)`]: {
      height: '30px',
      padding: '0 8px',
      fontSize: theme.fontSizes.sm,
      lineHeight: theme.other.lineHeights.sm
    }
  },

  buttonBackMargin: {
    margin: 0
  },
  // Heading
  headerHeading: {
    color: theme.colors.darkColors[0],
    lineHeight: theme.other.lineHeights.h2,
    fontSize: theme.other.fontSizes.h2,
    // Media query with value from theme
    [`@media (max-width: ${theme.breakpoints.md}px)`]: {
      fontSize: theme.fontSizes.lg
    }
  },
  // BreadCrumbs
  breadcrumbs: {
    color: theme.colors.darkColors[3],
    lineHeight: theme.other.lineHeights.sm,
    fontSize: theme.fontSizes.sm, // Media query with value from theme
    [`@media (max-width: ${theme.breakpoints.md}px)`]: {
      fontSize: theme.fontSizes.xs
    }
  },
  //  === End header styles ===

  // Background for sections
  mainBackgroundColor: {
    backgroundColor: theme.colors.darkColors[6]
  },

  // Table styles
  tableStyles: {
    fontFamily: 'Ubuntu',
    thead: {
      color: theme.colors.darkColors[2],
      fontSize: theme.fontSizes.sm,
      tr: {
        borderColor: theme.colors.darkColors[3]
      }
    },
    tbody: {
      color: theme.colors.darkColors[0],
      fontSize: theme.fontSizes.md
    },
    tr: {
      verticalAlign: 'top !important'
    }
  },

  // Secondary Button
  secondaryButton: {
    color: theme.colors.lightColors[0],
    backgroundColor: theme.colors.secondaryColors[0],
    fontWeight: 400,
    lineHeight: theme.other.lineHeights.md,
    height: '36px',
    padding: '0 32px',
    '&:hover': {
      backgroundColor: theme.colors.secondaryColors[1]
    }
  },
  // Primary Button
  primaryButton: {
    color: theme.colors.lightColors[0],
    backgroundColor: theme.colors.primaryColors[0],
    fontWeight: 400,
    lineHeight: theme.other.lineHeights.md,
    height: '36px',
    padding: '0 32px',
    '&:hover': {
      backgroundColor: theme.colors.primaryColors[1]
    }
  },

  // Normal Button
  normalButton: {
    color: theme.colors.darkColors[0],
    border: `1px solid ${theme.colors.darkColors[2]}`,
    backgroundColor: theme.colors.lightColors[0],
    fontWeight: 400,
    lineHeight: theme.other.lineHeights.md,
    height: '36px',
    padding: '0 32px',
    '&:hover': {
      backgroundColor: theme.colors.darkColors[4]
    },
    cursor: 'pointer'
  }
}));

export default useStyles;
